import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDomains, createDomain, updateDomain, deleteDomain, getCurrentUser } from '@/services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { useToast } from '../hooks/use-toast';
import { Plus, Pencil, Globe, ExternalLink, ChevronDown, ChevronRight, Radio, Power, PowerOff, Trash2, ArrowLeftRight } from 'lucide-react';
import TrocarContaGamDialog from '../components/TrocarContaGamDialog';
import type { CreateDomainInput, UpdateDomainInput, Domain } from '../types/domain';
import {useNavigate} from "react-router-dom";
import { Settings } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNetwork } from '@/contexts/NetworkContext';

interface DomainGroup {
    mainDomain: Domain;
    subdomains: Domain[];
}

export default function Dominios() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
    const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);
    const [domainToMove, setDomainToMove] = useState<Domain | null>(null);
    const [selectedMainDomain, setSelectedMainDomain] = useState<string | null>(null);
    const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
    const [formData, setFormData] = useState<CreateDomainInput>({
        domain: '',
        purpose: 'normal',
    });
    const [purposeTab, setPurposeTab] = useState<'normal' | 'capture'>('normal');

    const currentUser = getCurrentUser();
    // @ts-ignore
    const isOwner = currentUser?.owner === true || currentUser?.owner === 1;
    const isAdmin = currentUser?.role === 'admin';
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { networks, selected: selectedNetwork, format: formatConta } = useNetwork();

    // Query para listar domínios
    const { data: domains, isLoading, error } = useQuery({
        queryKey: ['domains'],
        queryFn: () => getDomains(),
    });

    // Lista de TLDs compostos conhecidos (apenas TLDs de 2 níveis como .com.br)
    const compositeTLDs = [
        // Brasil
        'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br', 'mil.br', 'art.br',
        // Reino Unido
        'co.uk', 'ac.uk', 'gov.uk', 'org.uk', 'me.uk',
        // Outros países
        'com.au', 'net.au', 'org.au', 'edu.au',
        'co.za', 'org.za', 'net.za',
        'com.ar', 'gov.ar', 'edu.ar',
        'com.mx', 'gob.mx', 'edu.mx',
        'com.co', 'net.co', 'org.co', 'edu.co', 'gov.co',
        'co.in', 'net.in', 'org.in', 'edu.in', 'gov.in',
        'com.cn', 'net.cn', 'org.cn', 'gov.cn',
        'co.jp', 'ne.jp', 'or.jp', 'ac.jp', 'go.jp',
        'co.nz', 'net.nz', 'org.nz', 'govt.nz',
        'com.sg', 'net.sg', 'org.sg', 'gov.sg', 'edu.sg',
        'com.my', 'net.my', 'org.my', 'gov.my', 'edu.my',
        'com.ph', 'net.ph', 'org.ph', 'gov.ph', 'edu.ph',
        'com.hk', 'net.hk', 'org.hk', 'gov.hk', 'edu.hk',
        // América Latina
        'com.pe', 'gob.pe', 'edu.pe', 'org.pe', 'net.pe',
        'com.ve', 'net.ve', 'org.ve', 'gob.ve', 'edu.ve',
        'com.cl', 'gob.cl', 'edu.cl', 'org.cl',
        'com.ec', 'net.ec', 'org.ec', 'gob.ec', 'edu.ec',
        'com.py', 'net.py', 'org.py', 'gov.py', 'edu.py'
    ];

    // Função para extrair o domínio principal
    const extractMainDomain = (domain: string): string => {
        const parts = domain.split('.');

        if (parts.length <= 2) {
            return domain; // já é domínio principal
        }

        // Verifica se tem TLD composto (ex: .com.br)
        const lastTwo = parts.slice(-2).join('.');
        if (compositeTLDs.includes(lastTwo)) {
            // Retorna os últimos 3 segmentos (ex: exemplo.com.br)
            return parts.slice(-3).join('.');
        }

        // Retorna os últimos 2 segmentos (ex: twintnews.com)
        return parts.slice(-2).join('.');
    };

    // Função para verificar se é subdomínio
    const isSubdomain = (domain: string): boolean => {
        const parts = domain.split('.');

        // Verifica se tem TLD composto
        const lastTwo = parts.slice(-2).join('.');
        if (compositeTLDs.includes(lastTwo)) {
            // Para TLDs compostos, precisa ter mais de 3 partes para ser subdomínio
            return parts.length > 3;
        }

        // Para TLDs simples, precisa ter mais de 2 partes
        return parts.length > 2;
    };

    const normalCount = useMemo(() => domains?.filter(d => (d.purpose || 'normal') === 'normal').length || 0, [domains]);
    const captureCount = useMemo(() => domains?.filter(d => d.purpose === 'capture').length || 0, [domains]);

    // Filtrar por purpose e agrupar domínios
    const filteredDomains = useMemo(() => {
        if (!domains) return [];
        return domains.filter(d => (d.purpose || 'normal') === purposeTab);
    }, [domains, purposeTab]);

    const groupedDomains = useMemo(() => {
        if (!filteredDomains.length) return [];

        const groups = new Map<string, DomainGroup>();
        const orphanSubdomains: Domain[] = []; // Subdomínios sem domínio principal

        // Primeiro, identificar todos os domínios principais
        filteredDomains.forEach((domain) => {
            if (!isSubdomain(domain.domain)) {
                const mainDomainName = domain.domain;
                groups.set(mainDomainName, {
                    mainDomain: domain,
                    subdomains: []
                });
            }
        });

        // Depois, associar subdomínios aos domínios principais existentes
        filteredDomains.forEach((domain) => {
            if (isSubdomain(domain.domain)) {
                const mainDomainName = extractMainDomain(domain.domain);

                if (groups.has(mainDomainName)) {
                    // Domínio principal existe, adicionar subdomínio
                    groups.get(mainDomainName)!.subdomains.push(domain);
                } else {
                    // Domínio principal não existe, é órfão
                    orphanSubdomains.push(domain);
                }
            }
        });

        // Ordenar subdomínios dentro de cada grupo
        groups.forEach(group => {
            group.subdomains.sort((a, b) => a.domain.localeCompare(b.domain));
        });

        // Converter para array e adicionar órfãos como grupos individuais
        const result = Array.from(groups.values());

        // Adicionar subdomínios órfãos como entradas individuais (sem agrupamento)
        orphanSubdomains.forEach(orphan => {
            result.push({
                mainDomain: orphan,
                subdomains: []
            });
        });

        // Ordenar por nome de domínio
        return result.sort((a, b) =>
            a.mainDomain.domain.localeCompare(b.mainDomain.domain)
        );
    }, [filteredDomains]);

    // Mutation para criar domínio
    const createDomainMutation = useMutation({
        mutationFn: createDomain,
        onSuccess: () => {
            toast({
                title: 'Domínio criado!',
                description: 'O novo domínio foi criado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['domains'] });
            queryClient.invalidateQueries({ queryKey: ['project'] });
            setIsCreateDialogOpen(false);
            setFormData({ domain: '', purpose: 'normal', network: undefined });
            setSelectedMainDomain(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar domínio',
                description: error.response?.data?.message || error.response?.data?.detail || 'Ocorreu um erro ao criar o domínio.',
            });
        },
    });

    // Mutation para atualizar domínio
    const updateDomainMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateDomainInput }) => updateDomain(id, data),
        onSuccess: () => {
            toast({
                title: 'Domínio atualizado!',
                description: 'O domínio foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['domains'] });
            queryClient.invalidateQueries({ queryKey: ['project'] });
            setIsEditDialogOpen(false);
            setSelectedDomain(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar domínio',
                description: error.response?.data?.message || 'Ocorreu um erro ao atualizar o domínio.',
            });
        },
    });

    // Mutation para apagar domínio (permanente)
    const deleteDomainMutation = useMutation({
        mutationFn: (id: number) => deleteDomain(id),
        onSuccess: () => {
            toast({
                title: 'Domínio apagado!',
                description: 'O domínio foi removido com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['domains'] });
            queryClient.invalidateQueries({ queryKey: ['project'] });
            setDomainToDelete(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao apagar domínio',
                description: error.response?.data?.message || error.response?.data?.detail || 'Ocorreu um erro ao apagar o domínio.',
            });
        },
    });

    // Ativa/desativa um domínio (reversível)
    const toggleDomainActive = (domain: Domain) => {
        updateDomainMutation.mutate({
            id: domain.id,
            data: { is_active: !domain.is_active },
        });
    };

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.domain.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, preencha o domínio.',
            });
            return;
        }

        if (formData.domain.length < 3) {
            toast({
                variant: 'destructive',
                title: 'Domínio muito curto',
                description: 'O domínio deve ter no mínimo 3 caracteres.',
            });
            return;
        }

        createDomainMutation.mutate(formData);
    };

    const handleEditSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedDomain) return;

        if (!formData.domain.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, preencha o domínio.',
            });
            return;
        }

        if (formData.domain.length < 3) {
            toast({
                variant: 'destructive',
                title: 'Domínio muito curto',
                description: 'O domínio deve ter no mínimo 3 caracteres.',
            });
            return;
        }

        updateDomainMutation.mutate({
            id: selectedDomain.id,
            data: { domain: formData.domain },
        });
    };

    const openEditDialog = (domain: Domain) => {
        setSelectedDomain(domain);
        setFormData({
            domain: domain.domain,
        });
        setIsEditDialogOpen(true);
    };

    const openCreateSubdomainDialog = (mainDomain: string) => {
        setSelectedMainDomain(mainDomain);
        setFormData({
            domain: '',
            // Subdomínio nasce na conta do domínio principal.
            network: domains?.find((d) => d.domain === mainDomain)?.network ?? selectedNetwork ?? undefined,
        });
        setIsCreateDialogOpen(true);
    };

    const toggleDomainExpansion = (domainName: string) => {
        setExpandedDomains(prev => {
            const newSet = new Set(prev);
            if (newSet.has(domainName)) {
                newSet.delete(domainName);
            } else {
                newSet.add(domainName);
            }
            return newSet;
        });
    };
    const navigate = useNavigate();


    const renderDomainRow = (domain: Domain, isMainDomain: boolean = false, subdominioCount: number = 0) => (
        <tr key={domain.id} className={`hover:bg-gray-50 ${!isMainDomain ? 'bg-gray-50/50' : ''}`}>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/setup/${domain.id}`);
                        }}
                        title="Gerenciar Setup"
                    >
                        <Settings className="h-4 w-4 text-blue-600" />
                    </Button>

                    {isMainDomain && subdominioCount > 0 && (
                        <button
                            onClick={() => toggleDomainExpansion(domain.domain)}
                            className="flex-shrink-0 hover:bg-gray-200 rounded p-1"
                        >
                            {expandedDomains.has(domain.domain) ? (
                                <ChevronDown className="h-4 w-4 text-gray-600" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-gray-600" />
                            )}
                        </button>
                    )}
                    {isMainDomain && subdominioCount === 0 && (
                        <div className="w-6" />
                    )}
                    {!isMainDomain && <div className="w-6 ml-6" />}
                    <div className={`flex-shrink-0 h-10 w-10 bg-gradient-to-br ${isMainDomain && subdominioCount > 0 ? 'from-blue-400 to-blue-600' : 'from-green-400 to-green-600'} rounded-full flex items-center justify-center`}>
                        <Globe className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <p className={`text-sm ${isMainDomain && subdominioCount > 0 ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                                {domain.domain}
                            </p>
                            <a
                                href={`https://${domain.domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-500">ID: {domain.id}</p>
                            <Badge variant="outline" className="text-xs font-normal" title="Conta GAM">
                                {formatConta(domain.network)}
                            </Badge>
                            {isMainDomain && subdominioCount > 0 && (
                                <Badge variant="outline" className="text-xs">
                                    {subdominioCount} subdomínio{subdominioCount > 1 ? 's' : ''}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                {domain.is_active ? (
                    <Badge variant="success">Ativo</Badge>
                ) : (
                    <Badge variant="destructive">Inativo</Badge>
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <p className="text-sm text-gray-600">
                    {new Date(domain.created_at).toLocaleDateString('pt-BR')}
                </p>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex justify-end gap-2">
                    {isMainDomain && subdominioCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCreateSubdomainDialog(domain.domain)}
                            title="Criar subdomínio"
                        >
                            <Plus className="h-4 w-4 text-green-600" />
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(domain)} title="Editar domínio">
                        <Pencil className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => setDomainToMove(domain)} title="Trocar conta GAM">
                            <ArrowLeftRight className="h-4 w-4 text-indigo-600" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDomainActive(domain)}
                        disabled={updateDomainMutation.isPending}
                        title={domain.is_active ? 'Desativar domínio' : 'Ativar domínio'}
                    >
                        {domain.is_active ? (
                            <PowerOff className="h-4 w-4 text-amber-600" />
                        ) : (
                            <Power className="h-4 w-4 text-green-600" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDomainToDelete(domain)}
                        title="Apagar domínio"
                    >
                        <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Domínios</h1>
                    <p className="mt-2 text-gray-600">Gerencie os domínios e subdomínios.</p>
                </div>
                <Button onClick={() => { setFormData({ domain: '', purpose: purposeTab, network: selectedNetwork ?? undefined }); setIsCreateDialogOpen(true); }}>
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Domínio
                </Button>
            </div>

            {/* Purpose Tabs */}
            {!isLoading && domains && domains.length > 0 && (
                <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 w-fit">
                    <button
                        onClick={() => setPurposeTab('normal')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            purposeTab === 'normal'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Globe className="h-4 w-4" />
                        Dominios
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                            purposeTab === 'normal' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                            {normalCount}
                        </span>
                    </button>
                    <button
                        onClick={() => setPurposeTab('capture')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                            purposeTab === 'capture'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Radio className="h-4 w-4" />
                        Dominios de Captura
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                            purposeTab === 'capture' ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                            {captureCount}
                        </span>
                    </button>
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="rounded-lg bg-white p-6 shadow">
                    <p className="text-center text-gray-600">Carregando domínios...</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-6">
                    <p className="text-red-800">Erro ao carregar domínios. Tente novamente.</p>
                </div>
            )}

            {/* Empty state for selected tab */}
            {!isLoading && !error && domains && domains.length > 0 && groupedDomains.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-lg bg-white border border-dashed border-gray-200 p-12">
                    {purposeTab === 'capture' ? (
                        <>
                            <Radio className="h-8 w-8 text-gray-300" />
                            <h3 className="mt-4 text-sm font-semibold text-gray-900">Nenhum dominio de captura</h3>
                            <p className="mt-1 text-xs text-gray-500">Crie um dominio com tipo "Captura" clicando em Novo Dominio.</p>
                        </>
                    ) : (
                        <>
                            <Globe className="h-8 w-8 text-gray-300" />
                            <h3 className="mt-4 text-sm font-semibold text-gray-900">Nenhum dominio normal</h3>
                            <p className="mt-1 text-xs text-gray-500">Crie um dominio clicando em Novo Dominio.</p>
                        </>
                    )}
                </div>
            )}

            {/* Domains Table */}
            {groupedDomains.length > 0 && (
                <div className="rounded-lg bg-white shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Domínio
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Criado em
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ações
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {groupedDomains.map((group) => (
                            <>
                                {renderDomainRow(group.mainDomain, true, group.subdomains.length)}
                                {expandedDomains.has(group.mainDomain.domain) &&
                                    group.subdomains.map((subdomain) => renderDomainRow(subdomain, false))
                                }
                            </>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}



            {/* Create Domain Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                setIsCreateDialogOpen(open);
                if (!open) setSelectedMainDomain(null);
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {selectedMainDomain ? `Criar Subdomínio de ${selectedMainDomain}` : 'Criar Novo Domínio'}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedMainDomain
                                ? 'Adicione um novo subdomínio.'
                                : 'Adicione um novo domínio a um projeto.'}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateSubmit} className="space-y-4 mt-4">

                        <div className="space-y-2">
                            <Label htmlFor="domain">
                                {selectedMainDomain ? 'Subdomínio *' : 'Domínio *'}
                            </Label>
                            {selectedMainDomain ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        id="domain"
                                        placeholder="es"
                                        value={formData.domain}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            domain: e.target.value.toLowerCase().trim()
                                        })}
                                        disabled={createDomainMutation.isPending}
                                        className="flex-1"
                                    />
                                    <span className="text-sm text-gray-600">.{selectedMainDomain}</span>
                                </div>
                            ) : (
                                <Input
                                    id="domain"
                                    placeholder="exemplo.com"
                                    value={formData.domain}
                                    onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase().trim() })}
                                    disabled={createDomainMutation.isPending}
                                />
                            )}
                            <p className="text-xs text-gray-500">
                                {selectedMainDomain
                                    ? 'Digite apenas o prefixo do subdomínio (ex: es, en, pt)'
                                    : 'Mínimo 3 caracteres, sem espaços'}
                            </p>
                        </div>

                        {/* Purpose selector */}
                        <div className="space-y-2">
                            <Label>Tipo do Domínio</Label>
                            <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, purpose: 'normal' })}
                                    className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                                        formData.purpose === 'normal'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Normal
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, purpose: 'capture' })}
                                    className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                                        formData.purpose === 'capture'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Captura
                                </button>
                            </div>
                            <p className="text-xs text-gray-500">
                                {formData.purpose === 'capture'
                                    ? 'Domínio de captura: usado somente para capturar leads de push, pode ser vinculado a grupos de notificação.'
                                    : 'Domínio normal: usado para ads e push notifications padrão.'}
                            </p>
                        </div>

                        {/* Conta GAM */}
                        <div className="space-y-2">
                            <Label htmlFor="network">Conta GAM</Label>
                            {selectedMainDomain ? (
                                // Subdomínio fica sempre na conta do domínio principal.
                                <p className="text-sm text-gray-900">{formatConta(formData.network)}</p>
                            ) : (
                                <Select
                                    value={formData.network ?? undefined}
                                    onValueChange={(v) => setFormData({ ...formData, network: v })}
                                    disabled={createDomainMutation.isPending}
                                >
                                    <SelectTrigger id="network">
                                        <SelectValue placeholder="Selecione a conta" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {networks.map((n) => (
                                            <SelectItem key={n.network_code} value={n.network_code}>
                                                {n.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            <p className="text-xs text-gray-500">
                                Conta onde os blocos e os anúncios deste domínio vão rodar. Depois que os
                                blocos forem gerados, a conta não pode mais ser trocada.
                            </p>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsCreateDialogOpen(false);
                                    setSelectedMainDomain(null);
                                }}
                                disabled={createDomainMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1"
                                disabled={createDomainMutation.isPending}
                                onClick={(e) => {
                                    if (selectedMainDomain) {
                                        e.preventDefault();
                                        const fullDomain = `${formData.domain}.${selectedMainDomain}`;
                                        createDomainMutation.mutate({
                                            ...formData,
                                            domain: fullDomain
                                        });
                                    }
                                }}
                            >
                                {createDomainMutation.isPending ? 'Criando...' : selectedMainDomain ? 'Criar Subdomínio' : 'Criar Domínio'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Domain Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar Domínio</DialogTitle>
                        <DialogDescription>Atualize o domínio.</DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-domain">Domínio *</Label>
                            <Input
                                id="edit-domain"
                                placeholder="exemplo.com"
                                value={formData.domain}
                                onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase().trim() })}
                                disabled={updateDomainMutation.isPending}
                            />
                            <p className="text-xs text-gray-500">Mínimo 3 caracteres, sem espaços</p>
                        </div>


                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setIsEditDialogOpen(false)}
                                disabled={updateDomainMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" className="flex-1" disabled={updateDomainMutation.isPending}>
                                {updateDomainMutation.isPending ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Confirmação de apagar domínio */}
            <ConfirmDialog
                open={!!domainToDelete}
                onOpenChange={(open) => { if (!open) setDomainToDelete(null); }}
                title="Apagar domínio"
                description={
                    domainToDelete
                        ? `Tem certeza que deseja apagar "${domainToDelete.domain}"? Esta ação é permanente e não pode ser desfeita. Para apenas pausar o domínio, use a opção Desativar.`
                        : ''
                }
                confirmLabel="Apagar"
                cancelLabel="Cancelar"
                variant="danger"
                isLoading={deleteDomainMutation.isPending}
                onConfirm={() => { if (domainToDelete) deleteDomainMutation.mutate(domainToDelete.id); }}
            />

            <TrocarContaGamDialog domain={domainToMove} onClose={() => setDomainToMove(null)} />

        </div>
    );
}